import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefact } from '@modules/common/components/SaveArtefact';
import { UnsavedChangesDialogContent } from '@modules/common/components/UnsavedChangesDialogContent';
import {
  useIsArtefactEditable,
  useIsSearchToFeedEnabled,
  useSubscribeToDashboard,
} from '@modules/common/hooks';
import { ArtefactType, Cron } from '@modules/common/types';
import {
  getLocalCronValueFromUTCCronValue,
  getSavedEmailsFromReportSchedule,
  getSlackChannelsFromReportSchedule,
} from '@modules/common/utils';
import { isSavedSearch } from '@modules/feed/components/SearchAnalysisToolbar/utils';
import { useUpdateScheduleDataInStore } from '@modules/feed/components/SearchSubscribeFeedback/hooks';
import { useSaveFeedAnalysis } from '@modules/feed/hooks';
import { useUpdateFeedAnalysisAction } from '@modules/feed/hooks/useUpdateFeedAnalysisAction';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { getSearchLink } from '@modules/feed/utils/getSearchLink';
import { SubscribeButton } from '@modules/subscribe/components/SubscribeButton';
import { SubscribeDrawerV2 } from '@modules/subscribe/components/SubscribeDrawerV2';
import { RemoveScheduleReportDialogContent } from '@modules/subscribe/components/SubscribeDrawerV2/RemoveScheduleReportDialogContent';
import { getIsSaveButtonDisabled } from '@modules/subscribe/components/SubscribeDrawerV2/utils';
import {
  OPEN_SUBSCRIBE_DRAWER_QUERY_PARAM,
  REMOVE_SUBSCRIPTION_MODAL_DESCRIPTION,
  REMOVE_SUBSCRIPTION_MODAL_TITLE,
} from '@modules/subscribe/constants';
import { useDeleteScheduleReportSubscription } from '@modules/subscribe/hooks/useDeleteScheduleReportSubscription';
import { Dialog } from '@mui/material';
import {
  ArtefactType as EdgeArtefactType,
  GetAnalysisByTokenDocument,
  SlackChannelInput,
  SubjectType,
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useGetUser } from 'v2/core/services/user';
import { SearchSubscribeDrawerContentHeader } from './SearchSubscribeDrawerContentHeader';
import { DAILY_FREQUENCY_DEFAULT_CRON_VALUE_V2 } from './constants';

const trackSearchSubscribeEvents = createAnalyticsTracker('searchSubscribe');

export const SearchSubscribeFeedbackV2 = () => {
  const navigate = useNavigate();
  const {
    id: userID,
    tenant: { slug: tenantSlug, id: tenantID },
  } = useGetUser();

  const { openModal, closeModal } = useMuiModalContext();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const searchAnalysis = useAnalysisStore((state) => state);
  const hasSearchChangesToBeSaved = useFeedStore(
    ({ visual: { hasChangesToBeSaved } = {} }) => hasChangesToBeSaved,
  );
  const prompt = useFeedStore(({ prompt }) => prompt);

  const reportSchedule = searchAnalysis?.reporting?.secondarySchedules?.[0];

  const { updateScheduleDataInStore, loading } = useUpdateScheduleDataInStore();

  const isSearchAnalysisEditable = useIsArtefactEditable({
    editToken: searchAnalysis.editToken,
    status: searchAnalysis.status,
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPDFReportingIncluded, setIsPDFReportingIncluded] = useState(true);
  const [cronSchedule, setCronSchedule] = useState<Cron | undefined>({
    realtime: reportSchedule?.cron.realtime,
    valueV2: getLocalCronValueFromUTCCronValue(reportSchedule?.cron.valueV2),
  });
  const [selectedSlackChannels, setSelectedSlackChannels] = useState<
    SlackChannelInput[]
  >([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);

  const isSubscribed = Boolean(reportSchedule);
  const isSearchDashboardSaved = isSavedSearch(searchAnalysis?.id);

  const [isSaveArtefactFormOpen, setIsSaveArtefactFormOpen] = useState(false);

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const artefactLink = searchAnalysis
    ? getSearchLink({
        name: searchAnalysis.title,
        token: searchAnalysis.viewToken,
        tenantSlug,
        isSearchToFeedEnabled,
      })
    : '';

  const openDrawer = () => {
    setIsDrawerOpen(true);
    trackSearchSubscribeEvents({
      eventName: 'Search Subscribe - Drawer Opened',
      artefactLink,
    });
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const refetchAnalysis = {
    query: GetAnalysisByTokenDocument,
    variables: {
      userID,
      tenantID: tenantID,
      token: searchAnalysis?.viewToken,
      includeScheduleOutbounds: true,
    },
  };

  const { subscribeToDashboard, isLoading: isSavingSubscription } =
    useSubscribeToDashboard({
      reportSchedule: reportSchedule,
      reportArtefact: {
        id: searchAnalysis?.id ?? '',
        type: EdgeArtefactType.Analysis,
        token: searchAnalysis?.viewToken ?? '',
      },
      subjectType: SubjectType.Analysis,
      refetchQueries: [refetchAnalysis],
      skipPDFInReport: !isPDFReportingIncluded,
    });

  const onSaveSubscription = async () => {
    if (!cronSchedule) {
      return;
    }

    try {
      await subscribeToDashboard({
        slackChannels: selectedSlackChannels,
        emails: selectedEmails,
        cron: cronSchedule,
      });
      closeDrawer();
      updateScheduleDataInStore();
      showSuccessNotification({
        contentComponent: `Your subscription has been ${
          isSubscribed ? 'updated' : 'added'
        }.`,
        props: {},
      });
      trackSearchSubscribeEvents({
        eventName: isSubscribed
          ? 'Search Subscribe - Subscription Updated'
          : 'Search Subscribe - Subscription Added',
        artefactLink,
        isPDFReportingIncluded,
        slackChannels: selectedSlackChannels,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to add the subscription.',
        props: {},
      });
    }
  };

  const { deleteScheduleReport, isLoading: isRemovingSubscription } =
    useDeleteScheduleReportSubscription({
      token: searchAnalysis?.viewToken ?? '',
    });

  const onRemoveSubscription = async () => {
    if (!reportSchedule) {
      return;
    }
    closeModal();
    try {
      await deleteScheduleReport(reportSchedule.id);
      updateScheduleDataInStore();
      showSuccessNotification({
        contentComponent: 'The subscription has been removed.',
        props: {},
      });
      setSelectedSlackChannels([]);
      closeDrawer();
      trackSearchSubscribeEvents({
        eventName: 'Search Subscribe - Subscription Removed',
        isPDFReportingIncluded,
        artefactLink,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to remove the subscription.',
        props: {},
      });
    }
  };

  const openRemoveSubscriptionDialog = () => {
    openModal({
      contentComponent: () => (
        <RemoveScheduleReportDialogContent
          onClose={() => closeModal()}
          title={REMOVE_SUBSCRIPTION_MODAL_TITLE}
          description={REMOVE_SUBSCRIPTION_MODAL_DESCRIPTION}
          onRemove={onRemoveSubscription}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  const { onSaveSearchDashboard } = useSaveFeedAnalysis();
  const { onUpdateSearchDashboard } = useUpdateFeedAnalysisAction();

  const onSaveNewSearch = () => {
    navigate({
      pathname: window.location.pathname.replace(`/${tenantSlug}`, ''),
      search: `?${OPEN_SUBSCRIBE_DRAWER_QUERY_PARAM}=true`,
    });
  };

  const onSaveUnsavedSearchChanges = async () => {
    closeModal();
    await onUpdateSearchDashboard();
    openDrawer();
  };

  const onClickSubscribeButton = () => {
    if (!isSearchDashboardSaved) {
      setIsSaveArtefactFormOpen(true);
      return;
    }

    if (isSearchDashboardSaved && hasSearchChangesToBeSaved) {
      openModal({
        contentComponent: () => (
          <UnsavedChangesDialogContent
            actionText="Subscribe to Feedback"
            onSave={onSaveUnsavedSearchChanges}
            onCancel={() => closeModal()}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      });
      return;
    }

    openDrawer();
  };

  const buttonTooltipText = () => {
    if (isSubscribed) {
      return `Update subscription to ${
        isSearchToFeedEnabled ? 'Feed' : 'Search'
      } Report`;
    }
    return isSearchDashboardSaved
      ? `Subscribe to ${isSearchToFeedEnabled ? 'Feed' : 'Search'} Report`
      : 'Please save changes before subscribing';
  };

  const onSelectedEmailsChange = (emails: string[]) => {
    setSelectedEmails(emails);
  };

  const handleSelectedSlackChannelsChange = (
    slackChannels: SlackChannelInput[],
  ) => {
    setSelectedSlackChannels(slackChannels);
    trackSearchSubscribeEvents({
      eventName: 'Search Subscribe - Slack Channels Selected',
      artefactLink,
      slackChannels,
    });
  };

  const handleTogglePDFReporting = () => {
    setIsPDFReportingIncluded(
      (isPDFReportingIncluded) => !isPDFReportingIncluded,
    );
  };

  useEffect(() => {
    if (!reportSchedule) {
      setSelectedSlackChannels([]);
      setSelectedEmails([]);
      setCronSchedule(DAILY_FREQUENCY_DEFAULT_CRON_VALUE_V2);
      return;
    }

    setSelectedSlackChannels(
      getSlackChannelsFromReportSchedule(reportSchedule),
    );
    setSelectedEmails(getSavedEmailsFromReportSchedule(reportSchedule));
    setCronSchedule({
      realtime: reportSchedule.cron.realtime,
      valueV2: getLocalCronValueFromUTCCronValue(reportSchedule.cron.valueV2),
    });
    setIsPDFReportingIncluded(
      !reportSchedule.metadata.defaultReportMetadata?.skipSnapshot,
    );
  }, [reportSchedule]);

  if (isSearchDashboardSaved && !isSearchAnalysisEditable) {
    return null;
  }

  const isSaveButtonDisabled = getIsSaveButtonDisabled({
    reportSchedule,
    cronSchedule,
    selectedEmails,
    selectedSlackChannels,
    isPDFReportingIncluded,
  });

  return (
    <>
      <SubscribeButton
        label="Subscribe"
        isDisabled={false}
        isSubscribed={isSubscribed}
        onClick={onClickSubscribeButton}
        tooltipText={buttonTooltipText()}
        isLoading={loading}
      />
      <SubscribeDrawerV2
        shouldShowRealtimeFrequency
        isOpen={isDrawerOpen}
        onOpen={openDrawer}
        onClose={closeDrawer}
        isSubscribed={Boolean(reportSchedule)}
        isSaveButtonDisabled={isSaveButtonDisabled}
        onSaveSubscription={onSaveSubscription}
        isSubscribeRequestInProgress={isSavingSubscription || loading}
        onUnsubscribeButtonClick={openRemoveSubscriptionDialog}
        isUnsubscribeRequestInProgress={isRemovingSubscription}
        selectedSlackChannels={selectedSlackChannels}
        onSelectedSlackChannelsChange={handleSelectedSlackChannelsChange}
        selectedEmails={selectedEmails}
        onSelectedEmailsChange={onSelectedEmailsChange}
        savedCronValue={{
          realtime: reportSchedule?.cron.realtime,
          valueV2: getLocalCronValueFromUTCCronValue(
            reportSchedule?.cron.valueV2,
          ),
        }}
        cronSchedule={cronSchedule}
        setCronSchedule={setCronSchedule}
        PDFReportingOptions={{
          isPDFReportingIncluded,
          setIsPDFReportingIncluded: handleTogglePDFReporting,
        }}
        headerNode={<SearchSubscribeDrawerContentHeader prompt={prompt} />}
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveArtefactFormOpen}
        onClose={() => setIsSaveArtefactFormOpen(false)}
      >
        <SaveArtefact
          onSubmit={onSaveSearchDashboard}
          artefactType={ArtefactType.Feed}
          onProceedToSubscribe={onSaveNewSearch}
          onClose={() => setIsSaveArtefactFormOpen(false)}
        />
      </Dialog>
    </>
  );
};
